import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sun, Truck } from "lucide-react";
import { IMAGES } from "@/constants/images";
import { Seo } from "@/context/SeoContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const VALUES = [
  { icon: Heart, title: "Apiculture douce", text: "Nous ne prélevons que le surplus des abeilles et laissons toujours de quoi passer l'hiver." },
  { icon: Sun, title: "Récolte à maturité", text: "Chaque miel est récolté quand les abeilles ont operculé leurs rayons — jamais avant, jamais chauffé." },
  { icon: Truck, title: "Circuits courts", text: "Du rucher à votre table : vente à la miellerie, à divers évènements, avec nos partenaires et sur cette boutique." },
];

export default function HistoirePage() {
  return (
    <div data-testid="histoire-page">
      <Seo pageKey="histoire" />
      <section className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-8">
        <motion.p {...fadeUp} className="eyebrow">L'Histoire de la Miellerie</motion.p>
        <motion.h1 {...fadeUp} className="mt-3 font-serif text-4xl font-semibold leading-tight tracking-tight text-bark sm:text-5xl">
          Des abeilles, des ruches, des champs de fleurs <span className="italic text-amber-deep">et vingt-trois ans de patience et passion</span>
        </motion.h1>
        <motion.blockquote {...fadeUp} className="mx-auto mt-10 max-w-2xl border-l-4 border-honey pl-6 text-left font-serif text-2xl italic leading-relaxed text-bark-soft">
          « On ne possède pas des abeilles. On les accompagne, et elles nous laissent un peu de leur or. »
          <footer className="mt-3 font-sans text-sm not-italic text-stone-mute">— Ernest, apiculteur et mentor</footer>
        </motion.blockquote>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {[IMAGES.blossoms, IMAGES.wildflower, IMAGES.botanical].map((src, i) => (
            <motion.div key={src} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="overflow-hidden rounded-2xl border border-amber-900/10">
              <img src={src} alt="Le rucher au fil des saisons" loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="histoire-timeline" className="mx-auto max-w-3xl px-4 py-16 sm:px-8">
        <motion.h2 {...fadeUp} className="mb-12 text-center font-serif text-2xl font-medium tracking-tight text-bark sm:text-3xl lg:text-4xl">
          Plus de deux décennies à l'écoute des abeilles
        </motion.h2>
        <div className="space-y-10">
          <motion.div {...fadeUp} data-testid="timeline-step-1" className="flex gap-6">
            <div className="flex flex-col items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-deep font-serif text-sm font-bold text-white shadow-md shadow-amber-900/20">
                2003
              </span>
            </div>
            <div className="pb-2 pt-1">
              <h3 className="font-serif text-xl font-semibold text-bark">Le début du voyage apicole</h3>
              <p className="mt-2 text-sm leading-relaxed text-bark-soft">J'installe quatre ruches au bout d'un champ, près d'une forêt de châtaigniers du côté d'Uriage. La première récolte tient dans une vingtaine de pots.</p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} data-testid="timeline-step-2" className="flex gap-6">
            <div className="flex flex-col items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-deep font-serif text-sm font-bold text-white shadow-md shadow-amber-900/20">
                2005
              </span>
            </div>
            <div className="pb-2 pt-1">
              <h3 className="font-serif text-xl font-semibold text-bark">Le virage professionnel</h3>
              <p className="mt-2 text-sm leading-relaxed text-bark-soft">Les abeilles deviennent ma révélation. Je décide de passer à l'étape d'après pour devenir professionnel. J'entame une formation de BPREA Apicole.</p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} data-testid="timeline-step-3" className="flex gap-6">
            <div className="flex flex-col items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-deep font-serif text-sm font-bold text-white shadow-md shadow-amber-900/20">
                2015
              </span>
            </div>
            <div className="pb-2 pt-1">
              <h3 className="font-serif text-xl font-semibold text-bark">L'atelier du rucher</h3>
              <p className="mt-2 text-sm leading-relaxed text-bark-soft">Ouverture de l'atelier : extraction à froid sur place, bougies coulées main et pain d'épices selon la recette de la grand-mère de Léon.</p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} data-testid="timeline-step-4" className="flex gap-6">
            <div className="flex flex-col items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-deep font-serif text-sm font-bold text-white shadow-md shadow-amber-900/20">
                2026
              </span>
            </div>
            <div className="pb-2 pt-1">
              <h3 className="font-serif text-xl font-semibold text-bark">120 ruches, 6 terroirs</h3>
              <p className="mt-2 text-sm leading-relaxed text-bark-soft">Des prairies fleuries de l'Isère, aux châtaigneraies, jusqu'aux ruchers de montagne, la petite famille d'abeilles est devenue grande — sans jamais perdre son âme.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-sand/60">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <motion.h2 {...fadeUp} className="mb-10 text-center font-serif text-2xl font-medium tracking-tight text-bark sm:text-3xl lg:text-4xl">
            Ce en quoi nous croyons
          </motion.h2>
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v) => (
              <motion.div key={v.title} {...fadeUp} data-testid={`value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
                <v.icon size={28} className="text-honey" />
                <h3 className="mt-4 font-serif text-xl font-semibold text-bark">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-bark-soft">{v.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-10 flex flex-col items-center gap-4">
            <Link to="/boutique" data-testid="histoire-cta-boutique" className="flex items-center gap-2 rounded-full bg-amber-deep px-7 py-3.5 font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95">
              Goûter la récolte <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
